// Background-page scroll lock for the widget's modal overlays. While a flow
// (checkout, paywall, donation, login) is on screen, the host page's scroll is
// locked so the page behind the fixed overlay can't scroll away.
//
// Ref-counted because the surfaces mount at independent points and can overlap
// (e.g. a dismissible paywall left open while the publisher fires openCheckout):
// the page stays locked while any surface holds the lock and is restored only
// when the last one releases. The host page's prior inline overflow is captured
// on the first acquire and written back on the last release — rather than
// cleared to '' — so a host page that sets its own inline overflow isn't
// clobbered.
//
// Both <html> and <body> are locked because host pages vary in which element is
// their actual scrolling box.
let lockCount = 0;
let previousHtmlOverflow = '';
let previousBodyOverflow = '';
let previousBodyPaddingRight = '';
let previousBodyMarginTop = '';

// The first child actually eligible to collapse its top margin with body's
// (see acquirePageScrollLock) — skips a display:none/absolute/fixed/floated
// leading element (e.g. an analytics <script> tag, common on publisher
// pages), which wouldn't itself collapse and would mask a real shift further
// down the DOM.
function firstInFlowChild(): Element | null {
  for (const child of document.body.children) {
    const style = getComputedStyle(child);
    if (style.display !== 'none' && style.position !== 'absolute' && style.position !== 'fixed' && style.float === 'none') {
      return child;
    }
  }
  return null;
}

export function acquirePageScrollLock(): void {
  lockCount += 1;
  if (lockCount > 1) {
    return;
  }
  // Hiding the scrollbar below widens the viewport's usable width by the
  // scrollbar's own track width, which visibly shifts ("jumps") any host-page
  // content sized off window/viewport width the instant the lock engages.
  // Measure that width before locking and pad the body back out by the same
  // amount so removing the scrollbar doesn't change the page's layout width.
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  // Setting overflow (below) also establishes a new block formatting context
  // on <body>, which stops a top margin that used to collapse into the page
  // (e.g. body's first in-flow child's margin-top collapsing with body's own)
  // from collapsing — visibly shifting all content down the instant the lock
  // engages, independent of the scrollbar-width shift above. The shift shows
  // up on the child's position, not body's own, so measure that instead and
  // cancel whatever net delta the lock introduces via body's own margin-top.
  const firstChild = firstInFlowChild();
  const topBefore = firstChild ? firstChild.getBoundingClientRect().top : 0;
  previousHtmlOverflow = document.documentElement.style.overflow;
  previousBodyOverflow = document.body.style.overflow;
  previousBodyPaddingRight = document.body.style.paddingRight;
  previousBodyMarginTop = document.body.style.marginTop;
  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';
  if (scrollbarWidth > 0) {
    // Read as a px value and added to, not computed against — a host page
    // using a percentage padding-right would compound incorrectly here, but
    // that's an existing limitation of this same technique in Bootstrap/MUI.
    const existingPaddingRight = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
    document.body.style.paddingRight = `${existingPaddingRight + scrollbarWidth}px`;
  }
  const verticalShift = firstChild ? firstChild.getBoundingClientRect().top - topBefore : 0;
  if (verticalShift !== 0) {
    const existingMarginTop = parseFloat(getComputedStyle(document.body).marginTop) || 0;
    document.body.style.marginTop = `${existingMarginTop - verticalShift}px`;
  }
}

export function releasePageScrollLock(): void {
  if (lockCount === 0) {
    return;
  }
  lockCount -= 1;
  if (lockCount > 0) {
    return;
  }
  document.documentElement.style.overflow = previousHtmlOverflow;
  document.body.style.overflow = previousBodyOverflow;
  document.body.style.paddingRight = previousBodyPaddingRight;
  document.body.style.marginTop = previousBodyMarginTop;
}
